import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  useEffect(() => { document.title = 'Contact Us - SIYAK'; }, []);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/contact`, form);
      toast.success('Message sent successfully!');
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="bg-navy-50 min-h-screen">
          <div className="max-w-2xl mx-auto px-4 py-16 text-center">
            <div className="bg-white rounded-lg p-12 shadow-sm">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy-900 mb-4">
                Message Sent Successfully
              </h1>
              <p className="text-muted-foreground mb-8">
                Thank you for contacting us. We'll get back to you as soon as possible.
              </p>
              <div className="text-left max-w-xl mx-auto mb-6">
                <p className="text-sm font-medium">Subject</p>
                <p className="text-sm whitespace-pre-wrap break-words mb-3">{form.subject}</p>
                <p className="text-sm font-medium">Message</p>
                <div className="p-4 bg-navy-50 rounded-lg text-sm whitespace-pre-wrap break-words">{form.message}</div>
              </div>
              <Button onClick={() => {
                setSubmitted(false);
                setForm({ name: '', email: '', phone: '', subject: '', message: '' });
              }}>
                Send Another Message
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-navy-50 min-h-screen" data-testid="contact-page">
        {/* Header */}
        <div className="bg-navy-900 text-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Get in Touch
            </h1>
            <p className="text-navy-200 text-lg max-w-2xl mx-auto">
              Have a question or suggestion? We'd love to hear from you.
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 md:px-8 py-12 md:py-16">
          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-2xl">Send us a Message</CardTitle>
              <p className="text-muted-foreground text-sm mt-2">We'll get back to you as soon as possible</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      placeholder="Full Name (e.g. John Smith)"
                      value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      placeholder="+44 7XXX XXXXXX"
                      value={form.phone}
                      onChange={(e) => setForm({...form, phone: e.target.value})}
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      required
                      placeholder="How can we help?"
                      value={form.subject}
                      onChange={(e) => setForm({...form, subject: e.target.value})}
                      data-testid="contact-subject-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    required
                    placeholder="Tell us more about your inquiry..."
                    value={form.message}
                    onChange={(e) => setForm({...form, message: e.target.value})}
                    rows={6}
                    data-testid="contact-message-input"
                  />
                </div>
                <Button type="submit" size="lg" disabled={submitting} data-testid="contact-submit-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};
